import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useWindowDimensions } from "src/hooks/useWindowdimension";
import {
  Autoplay,
  Navigation,
  Pagination,
  Virtual,
  Thumbs,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { breakpoints } from "src/config/responsiveSize";
import { ChevronLeft, ChevronRight } from "lucide-react";
import GridCard from "./GridCard";
import ProductSkeleton from "./ProductSkeleton";

const MegaMenuSlider = ({
  sliderPerView = 3,
  sliderImages = [],
  centeredSlide = false,
  closeMenu
}) => {
  const isLoading = sliderImages.length === 0;
  const swiperRef = useRef(null);
  const { width } = useWindowDimensions();

  const getResponsiveSlidesPerView = () => {
    if (width < 640) return 2;
    if (width < 1024) return 3;
    return 4;
  };
  
  // Force autoplay to restart whenever content loads
  useEffect(() => {
    if (!isLoading && swiperRef.current && swiperRef.current.swiper) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const swiper = swiperRef.current.swiper;
        
        // Reinitialize autoplay
        if (swiper.autoplay) {
          swiper.autoplay.stop();
          swiper.autoplay.start();
        }
        
        // Update swiper to recognize new slides
        swiper.update();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, sliderImages]);

  return (
    <div className="py-0">
      <div className="mx-auto p-0">
        <div className="flex justify-between items-center pb-6 -mt-2">
          <div className="">
            <h4 className="font-outfitMedium text-[22px] text-black">Trend this week</h4>
          </div>
          <div className="flex justify-end items-center space-x-2">
            <button className={`prev ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button className={`next ${isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <Swiper
          ref={swiperRef}
          modules={[Autoplay, Virtual, Navigation, Pagination, Thumbs]}
          spaceBetween={12}
          slidesPerView={getResponsiveSlidesPerView()}
          loop={!isLoading && sliderImages.length > 1}
          centeredSlides={centeredSlide}
          autoplay={{
            delay: 2000,
            disableOnInteraction: false,
            stopOnLastSlide: false,
          }}
          className="relative"
          pagination={false}
          observer={true}
          observeParents={true}
          updateOnWindowResize={true}
          breakpoints={breakpoints}
          style={{ height: "100%" }}
          grabCursor={!isLoading}
          navigation={{
            nextEl: ".next",
            prevEl: ".prev",
          }}
          watchSlidesProgress={true}
          onSwiper={(swiper) => {
            swiperRef.current = { swiper };
          }}
        >
          {isLoading ? (
            [...Array(6)].map((_, index) => (
              <SwiperSlide key={`skeleton-${index}`} className="relative h-full">
                <div className="bg-white rounded-3xl w-full h-full">
                  <ProductSkeleton height="h-[150px]" />
                </div>
              </SwiperSlide>
            ))
          ) : (
            sliderImages.map((item, index) => (
              <SwiperSlide key={`content-${index}`} className="relative h-full flex items-center justify-center">
                <div className="bg-white rounded-2xl w-full h-auto">
                  <GridCard data={item} closeMenu={closeMenu} hideCategory={true} hideTags={true} height="aspect-square" />
                </div>
              </SwiperSlide>
            ))
          )}
        </Swiper>
      </div>
    </div>
  );
};

export default MegaMenuSlider;
